// src/components/Profile.js

import React, { useState, useEffect, useRef } from 'react';
import { api, getUserInfo, generateAvatar as apiGenerateAvatar, getUserAchievements } from '../api';
import styles from './Profile.module.css';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext';
import AvatarGeneratorModal from './AvatarGeneratorModal';
import defaultAvatar from '../assets/default-profile.png';
import { useTranslation } from 'react-i18next';
import PersonalInformationSection from './PersonalInformationSection';
import BillingSection from './BillingSection';
import GeneralSettingsSection from './GeneralSettingsSection';

const BASE_URL = 'https://quiron.app';

function Profile({ isSidebarOpen, sidebarWidth }) {
  const { t } = useTranslation();
  const { user, setUser } = useUser();
  const { addNotification } = useNotification();
  const [activeTab, setActiveTab] = useState('general');
  const [activeSubTab, setActiveSubTab] = useState('balance');
  const [balance, setBalance] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(user?.profile_picture || defaultAvatar);
  const [prompt, setPrompt] = useState('');
  const [avatarGeneratorOpen, setAvatarGeneratorOpen] = useState(false);
  const [tempAvatar, setTemporaryAvatar] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [avatarHistory, setAvatarHistory] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const fileInputRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (user && user.id) {
      const fetchInitialProfileData = async () => {
        try {
          const [balanceResponse, avatarHistoryResponse, achievementsResponse] = await Promise.all([
            api.get('/billing/dracmas/balance'),
            api.get('/user/avatar-history'),
            getUserAchievements()
          ]);
          if (isMounted.current) {
            setBalance(balanceResponse.data.balance);
            setAvatarHistory(avatarHistoryResponse.data);
            setAchievements(achievementsResponse.data);
          }
        } catch (err) {
          if (isMounted.current) addNotification(err.message, 'error');
          console.error('Erro ao carregar dados do perfil:', err);
        }
      };
      fetchInitialProfileData();
    }
  }, [user?.id, addNotification]);

  useEffect(() => {
    if (activeTab === 'billing' && user && user.id) {
      const fetchBillingHistories = async () => {
        if (isMounted.current) {
          setUsageHistory([]);
          setPaymentHistory([]);
        }
        try {
          const [usageResponse, paymentResponse] = await Promise.all([
            api.get('/user/history/usage'),
            api.get('/billing/history/payment')
          ]);
          if (isMounted.current) {
            setUsageHistory(usageResponse.data);
            setPaymentHistory(paymentResponse.data);
          }
        } catch (err) {
          if (isMounted.current) addNotification(err.message, 'error');
          console.error('Erro ao carregar históricos de faturamento:', err);
        }
      };
      fetchBillingHistories();
    }
  }, [activeTab, user?.id, addNotification]);

  useEffect(() => {
    if (user && user.profile_picture) {
      if (user.profile_picture === 'default-profile.png') {
        setPreview(defaultAvatar);
      } else {
        setPreview(`${BASE_URL}/static/uploads/profile_pictures/${user.profile_picture}`);
      }
    } else {
      setPreview(defaultAvatar);
    }
  }, [user?.profile_picture]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      addNotification(t('selectPhotoToUpload'), 'error');
      return;
    }
    const uploadFormData = new FormData();
    uploadFormData.append('file', selectedFile);
    try {
      await api.post('/user/upload-profile-picture', uploadFormData);
      const userData = await getUserInfo();
      setUser(userData);
      setSelectedFile(null);
      addNotification(t('profilePictureUpdatedSuccess'), 'success');
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      addNotification(err.message || t('errorUploadingPhoto'), 'error');
    }
  };

  const handleGenerateAvatar = async () => {
    if (!prompt) {
      addNotification(t('enterPromptToGenerateAvatar'), 'error');
      return;
    }
    setIsGenerating(true);
    try {
      const response = await apiGenerateAvatar(prompt, true);
      if (isMounted.current) {
        setTemporaryAvatar(response.temp_avatar_url);
        addNotification(t('avatarGeneratedSuccess'), 'success');
      }
    } catch (err) {
      console.error('Erro ao gerar avatar:', err);
      addNotification(err.message || t('errorGeneratingAvatar'), 'error');
    } finally {
      if (isMounted.current) setIsGenerating(false);
    }
  };

  const handleSaveAvatar = async () => {
    if (!tempAvatar) {
      addNotification(t('noAvatarToSave'), 'error');
      return;
    }
    try {
      const filename = tempAvatar.split('/').pop();
      await api.post('/user/save-avatar', { filename });
      const userData = await getUserInfo();
      setUser(userData);
      const avatarHistoryResponse = await api.get('/user/avatar-history');
      setAvatarHistory(avatarHistoryResponse.data);
      
      setTemporaryAvatar(null);
      setAvatarGeneratorOpen(false);
      addNotification(t('avatarSavedSuccess'), 'success');
    } catch (err) {
      console.error('Erro ao salvar avatar:', err);
      addNotification(err.message || t('errorSavingAvatar'), 'error');
    }
  };

  const handleDiscardAvatar = () => {
    setTemporaryAvatar(null);
    addNotification(t('avatarDiscarded'), 'info');
  };

  const handleResetProfilePicture = async () => {
    try {
      await api.post('/user/reset-profile-picture');
      const userData = await getUserInfo();
      setUser(userData);
      addNotification(t('profilePictureResetSuccess'), 'success');
    } catch (err) {
      console.error('Erro ao resetar foto:', err);
      addNotification(err.message || t('errorResettingProfilePicture'), 'error');
    }
  };

  const handleCloseModal = () => {
    setAvatarGeneratorOpen(false);
    setTemporaryAvatar(null);
    setPrompt('');
  };

  const handleRemoveAvatar = async (index) => {
    const avatarToRemove = avatarHistory[index];
    if (!avatarToRemove) return;
    const filename = avatarToRemove.split('/').pop();
    try {
      await api.delete(`/user/avatar-history/${filename}`);
      setAvatarHistory((prev) => prev.filter((_, i) => i !== index));
      addNotification(t('avatarRemovedFromHistory'), 'success');
    } catch (err) {
      console.error('Erro ao remover avatar:', err);
      addNotification(err.message || t('errorRemovingAvatar'), 'error');
    }
  };

  const refreshHistories = async () => {
    addNotification(t('historiesClearedLocally'), 'info');
    try {
      const [usageResponse, paymentResponse] = await Promise.all([
        api.get('/user/history/usage'),
        api.get('/billing/history/payment')
      ]);
      setUsageHistory(usageResponse.data);
      setPaymentHistory(paymentResponse.data);
      addNotification(t('historiesUpdatedFromServer'), 'success');
    } catch (err) {
      console.error('Erro ao recarregar históricos:', err);
      addNotification(err.message || t('errorReloadingHistories'), 'error');
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return <GeneralSettingsSection />;
      case 'personal':
        return (
            <>
                <PersonalInformationSection />
                <div className="profile-section">
                    <h3 className={styles['tab-section-title']}>{t('achievements')}</h3>
                    {achievements.length > 0 ? (
                        <ul>
                            {achievements.map(ach => (
                                <li key={ach.id}>{ach.badge_code}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className={styles.noHistoryMessage}>{t('noAchievementsYet')}</p>
                    )}
                </div>
            </>
        );
      case 'billing':
        return (
          <BillingSection
            activeSubTab={activeSubTab}
            setActiveSubTab={setActiveSubTab}
            balance={balance}
            usageHistory={usageHistory}
            paymentHistory={paymentHistory}
            refreshHistories={refreshHistories}
            user={user}
          />
        );
      default:
        return <GeneralSettingsSection />;
    }
  };

  return (
    <div
      className={`${styles.profileContainer} ${!isSidebarOpen ? styles.adjustedForClosedSidebar : ''}`}
    >
      <h2 className={styles.profileTitle}>{t('profileTitle')}</h2>
      <div className={styles.centeredContainer}>
        <div className={styles.tabContainer}>
          <button
            className={activeTab === 'general' ? styles.activeTab : ''}
            onClick={() => setActiveTab('general')}
          >
            {t('generalSettings')}
          </button>
          <button
            className={activeTab === 'personal' ? styles.activeTab : ''}
            onClick={() => setActiveTab('personal')}
          >
            {t('personalInformation')}
          </button>
          <button
            className={activeTab === 'billing' ? styles.activeTab : ''}
            onClick={() => setActiveTab('billing')}
          >
            {t('billing')}
          </button>
          </div>

          <div className={styles.tabContent}>
            {renderContent()}
          </div>

          {activeTab === 'personal' && user && (
            <>
              <div className={styles.profilePictureContainer}>
                <img 
                  src={preview} 
                  alt={t('profilePictureAlt')}
                  className={styles.profilePicture} 
                  onError={(e) => { e.target.onerror = null; e.target.src = defaultAvatar;}}
                />
              </div>
              <div className={styles.buttonGroup}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <button onClick={() => fileInputRef.current.click()} className={`${styles['button-base']} ${styles['button-secondary']}`}>
                  {t('uploadPhoto')}
                </button>
                {selectedFile && (
                  <button onClick={handleUpload} className={`${styles['button-base']} ${styles['button-primary']}`}>
                    {t('savePhoto')}
                  </button>
                )}
                <button onClick={() => setAvatarGeneratorOpen(true)} className={`${styles['button-base']} ${styles['button-primary']}`}>
                  <span role="img" aria-label={t('magicWandEmoji')}>✨</span> {t('generateAvatar')}
                </button>
                <button onClick={handleResetProfilePicture} className={`${styles['button-base']} ${styles['button-neutral']}`}>
                  {t('resetPhoto')}
                </button>
              </div>

              <AvatarGeneratorModal
                isOpen={avatarGeneratorOpen}
                onClose={handleCloseModal}
                prompt={prompt}
                setPrompt={setPrompt}
                handleGenerateAvatar={handleGenerateAvatar}
                isGenerating={isGenerating}
                tempAvatar={tempAvatar}
                setTempAvatar={setTemporaryAvatar}
                handleSaveAvatar={handleSaveAvatar}
                handleDiscardAvatar={handleDiscardAvatar}
                avatarHistory={avatarHistory}
                handleRemoveAvatar={handleRemoveAvatar}
                addNotification={addNotification}
              />
            </>
          )}
          {activeTab === 'personal' && !user && (
            <div className={styles.profilePictureContainer}>
               <img src={defaultAvatar} alt={t('defaultProfilePictureAlt')} className={styles.profilePicture} />
            </div>
          )}
        </div>
      </div>
  );
}

export default Profile;